import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserTable = ({ 
  users, 
  selectedUsers, 
  onSelectUser, 
  onSelectAll, 
  onEditUser, 
  onDeleteUser, 
  onToggleStatus, 
  onResetPassword,
  sortConfig,
  onSort 
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Activo' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactivo' },
      blocked: { color: 'bg-red-100 text-red-800', label: 'Bloqueado' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-blue-100 text-blue-800', label: 'Administrador' },
      operator: { color: 'bg-purple-100 text-purple-800', label: 'Operador' },
      auditor: { color: 'bg-orange-100 text-orange-800', label: 'Auditor' }
    };
    
    const config = roleConfig?.[role] || roleConfig?.operator;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-gray-400" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString)?.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedUsers?.length === users?.length && users?.length > 0}
                  onChange={onSelectAll}
                  indeterminate={selectedUsers?.length > 0 && selectedUsers?.length < users?.length}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Nombre</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('email')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Email</span>
                  {getSortIcon('email')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('role')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Rol</span>
                  {getSortIcon('role')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Estado</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('dailyLimit')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Límite Diario</span>
                  {getSortIcon('dailyLimit')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('lastLogin')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Último Acceso</span>
                  {getSortIcon('lastLogin')}
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users?.map((user) => (
              <tr
                key={user?.id}
                className={`hover:bg-muted/50 transition-smooth ${
                  selectedUsers?.includes(user?.id) ? 'bg-primary/5' : ''
                }`}
                onMouseEnter={() => setHoveredRow(user?.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => onSelectUser(user?.id)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary-foreground">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{user?.name}</div>
                      {user?.twoFactorEnabled && (
                        <div className="flex items-center space-x-1 mt-1">
                          <Icon name="Shield" size={12} className="text-green-600" />
                          <span className="text-xs text-green-600">2FA</span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-foreground">{user?.email}</div>
                </td>
                <td className="px-4 py-4">
                  {getRoleBadge(user?.role)}
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-foreground">
                    {user?.dailyQueriesUsed}/{user?.dailyLimit}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div
                      className={`h-1.5 rounded-full ${
                        (user?.dailyQueriesUsed / user?.dailyLimit) > 0.8 
                          ? 'bg-red-500' 
                          : (user?.dailyQueriesUsed / user?.dailyLimit) > 0.6 
                            ? 'bg-yellow-500' :'bg-green-500'
                      }`}
                      style={{ width: `${Math.min((user?.dailyQueriesUsed / user?.dailyLimit) * 100, 100)}%` }}
                    />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-foreground">{formatDate(user?.lastLogin)}</div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditUser(user)}
                      iconName="Edit"
                      iconSize={14}
                      className="text-muted-foreground hover:text-foreground"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus(user)}
                      iconName={user?.status === 'blocked' ? "Unlock" : "Lock"}
                      iconSize={14}
                      className={`${
                        user?.status === 'blocked' ?'text-green-600 hover:text-green-700' :'text-yellow-600 hover:text-yellow-700'
                      }`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onResetPassword(user)}
                      iconName="Key"
                      iconSize={14}
                      className="text-blue-600 hover:text-blue-700"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser(user)}
                      iconName="Trash2"
                      iconSize={14}
                      className="text-red-600 hover:text-red-700"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {users?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No se encontraron usuarios</h3>
          <p className="text-muted-foreground">Ajusta los filtros o agrega nuevos usuarios al sistema.</p>
        </div>
      )}
    </div>
  );
};

export default UserTable;